<template>
  <div class="min-h-screen bg-gray-50 p-6 pt-24 lg:pt-8" style="direction: rtl; text-align: right;">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">إدارة مجموعات الصلاحيات</h1>
        <p class="mt-2 text-sm text-gray-600">إنشاء وإدارة مجموعات الصلاحيات لفرعك</p>
      </div>

      <!-- Create Group Button -->
      <div class="mb-6">
        <button
          @click="openCreateModal"
          class="bg-customHafadhColor text-white px-4 py-2 rounded-md hover:bg-customHafadhColorHover transition-colors"
        >
          + إضافة مجموعة جديدة
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-customHafadhColor"></div>
        <p class="mt-4 text-gray-600">جاري تحميل البيانات...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-800">{{ error }}</p>
      </div>

      <!-- Groups Table -->
      <div v-if="!loading && groups.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-customHafadhColor">
            <tr>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">اسم المجموعة</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الوصف</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">عدد الصلاحيات</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">عدد المستخدمين</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الحالة</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الإجراءات</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="group in groups" :key="group.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ group.name }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">{{ group.description || '-' }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ group.permissions_count }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ group.users_count }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="group.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ group.is_active ? 'نشط' : 'غير نشط' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="openEditModal(group)"
                  class="text-customHafadhColor hover:text-customHafadhColorHover mr-4"
                >
                  تعديل
                </button>
                <button
                  @click="openPermissionsModal(group)"
                  class="text-blue-600 hover:text-blue-900 mr-4"
                >
                  الصلاحيات
                </button>
                <button
                  @click="deleteGroup(group.id)"
                  class="text-red-600 hover:text-red-900"
                >
                  حذف
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && groups.length === 0" class="bg-white rounded-lg shadow p-12 text-center">
        <p class="text-gray-500">لا توجد مجموعات صلاحيات</p>
      </div>

      <!-- Create/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeModal">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{ editingGroup ? 'تعديل المجموعة' : 'إضافة مجموعة جديدة' }}
              </h3>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">اسم المجموعة</label>
                  <input
                    v-model="formData.name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
                    placeholder="أدخل اسم المجموعة"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                  <textarea
                    v-model="formData.description"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
                    placeholder="أدخل وصف المجموعة"
                  ></textarea>
                </div>
                <div>
                  <label class="flex items-center">
                    <input
                      v-model="formData.is_active"
                      type="checkbox"
                      class="ml-2"
                    />
                    <span class="text-sm text-gray-700">نشط</span>
                  </label>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                @click="saveGroup"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColorHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customHafadhColor sm:ml-3 sm:w-auto sm:text-sm"
              >
                حفظ
              </button>
              <button
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Permissions Modal -->
      <div v-if="showPermissionsModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closePermissionsModal">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closePermissionsModal"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                إدارة صلاحيات: {{ selectedGroup?.name }}
              </h3>
              <div v-if="pagesLoading" class="text-center py-8">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-customHafadhColor"></div>
              </div>
              <div v-else class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">الصفحة</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div class="flex items-center justify-center">
                          <input
                            ref="readCheckbox"
                            type="checkbox"
                            :checked="isAllChecked('read')"
                            @change="toggleAllPermissions('read', $event.target.checked)"
                            class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded cursor-pointer"
                            title="تحديد/إلغاء تحديد الكل"
                          />
                          <span class="mr-2">قراءة</span>
                        </div>
                      </th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div class="flex items-center justify-center">
                          <input
                            ref="createCheckbox"
                            type="checkbox"
                            :checked="isAllChecked('create')"
                            @change="toggleAllPermissions('create', $event.target.checked)"
                            class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded cursor-pointer"
                            title="تحديد/إلغاء تحديد الكل"
                          />
                          <span class="mr-2">إنشاء</span>
                        </div>
                      </th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div class="flex items-center justify-center">
                          <input
                            ref="updateCheckbox"
                            type="checkbox"
                            :checked="isAllChecked('update')"
                            @change="toggleAllPermissions('update', $event.target.checked)"
                            class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded cursor-pointer"
                            title="تحديد/إلغاء تحديد الكل"
                          />
                          <span class="mr-2">تعديل</span>
                        </div>
                      </th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div class="flex items-center justify-center">
                          <input
                            ref="deleteCheckbox"
                            type="checkbox"
                            :checked="isAllChecked('delete')"
                            @change="toggleAllPermissions('delete', $event.target.checked)"
                            class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded cursor-pointer"
                            title="تحديد/إلغاء تحديد الكل"
                          />
                          <span class="mr-2">حذف</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="page in reversedPages" :key="page.id">
                      <td class="px-4 py-3 text-sm text-gray-900">{{ page.name }}</td>
                      <td class="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          :checked="getPagePermission(page.id, 'read')"
                          @change="updatePermission(page.id, 'read', $event.target.checked)"
                          class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded"
                        />
                      </td>
                      <td class="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          :checked="getPagePermission(page.id, 'create')"
                          @change="updatePermission(page.id, 'create', $event.target.checked)"
                          class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded"
                        />
                      </td>
                      <td class="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          :checked="getPagePermission(page.id, 'update')"
                          @change="updatePermission(page.id, 'update', $event.target.checked)"
                          class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded"
                        />
                      </td>
                      <td class="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          :checked="getPagePermission(page.id, 'delete')"
                          @change="updatePermission(page.id, 'delete', $event.target.checked)"
                          class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                @click="savePermissions"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColorHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customHafadhColor sm:ml-3 sm:w-auto sm:text-sm"
              >
                حفظ الصلاحيات
              </button>
              <button
                @click="closePermissionsModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'PermissionGroupsManagement',
  data() {
    return {
      groups: [],
      pages: [],
      loading: false,
      pagesLoading: false,
      error: null,
      showModal: false,
      showPermissionsModal: false,
      editingGroup: null,
      selectedGroup: null,
      formData: {
        name: '',
        description: '',
        is_active: true,
      },
      permissionsData: {}, // { pageId: { read: bool, create: bool, update: bool, delete: bool } }
    };
  },
  computed: {
    // Reverse pages order for display (last page first)
    reversedPages() {
      return [...this.pages].reverse();
    },
  },
  mounted() {
    this.loadGroups();
    this.loadPages();
  },
  updated() {
    // Update indeterminate state for checkboxes
    this.$nextTick(() => {
      this.updateIndeterminateState();
    });
  },
  watch: {
    permissionsData: {
      handler() {
        this.$nextTick(() => {
          this.updateIndeterminateState();
        });
      },
      deep: true,
    },
  },
  methods: {
    async loadGroups() {
      this.loading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.permission_groups_list,
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        if (response.data && response.data.result && response.data.result.data) {
          this.groups = response.data.result.data.groups || [];
        } else if (response.data && response.data.data) {
          this.groups = response.data.data.groups || [];
        }
      } catch (error) {
        console.error('Error loading groups:', error);
        this.error = error.response?.data?.result?.mesAr || 'حدث خطأ أثناء تحميل البيانات';
      } finally {
        this.loading = false;
      }
    },
    async loadPages() {
      this.pagesLoading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.pages_list,
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        if (response.data && response.data.result && response.data.result.data) {
          this.pages = response.data.result.data.pages || [];
        } else if (response.data && response.data.data) {
          this.pages = response.data.data.pages || [];
        }
      } catch (error) {
        console.error('Error loading pages:', error);
      } finally {
        this.pagesLoading = false;
      }
    },
    openCreateModal() {
      this.editingGroup = null;
      this.formData = {
        name: '',
        description: '',
        is_active: true,
      };
      this.showModal = true;
    },
    openEditModal(group) {
      this.editingGroup = group;
      this.formData = {
        name: group.name,
        description: group.description || '',
        is_active: group.is_active,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingGroup = null;
      this.formData = {
        name: '',
        description: '',
        is_active: true,
      };
    },
    async saveGroup() {
      try {
        const token = localStorage.getItem('token');
        
        if (this.editingGroup) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.permission_groups_update + this.editingGroup.id,
            this.formData,
            {
              headers: {
                'X-Authorization': token,
              },
            }
          );
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.permission_groups_create,
            this.formData,
            {
              headers: {
                'X-Authorization': token,
              },
            }
          );
        }
        
        this.closeModal();
        this.loadGroups();
      } catch (error) {
        console.error('Error saving group:', error);
        alert(error.response?.data?.result?.mesAr || 'حدث خطأ أثناء الحفظ');
      }
    },
    async deleteGroup(groupId) {
      if (!confirm('هل أنت متأكد من حذف هذه المجموعة؟')) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.permission_groups_delete + groupId,
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        this.loadGroups();
      } catch (error) {
        console.error('Error deleting group:', error);
        alert(error.response?.data?.result?.mesAr || 'حدث خطأ أثناء الحذف');
      }
    },
    async openPermissionsModal(group) {
      this.selectedGroup = group;
      this.permissionsData = {};
      
      // Ensure pages are loaded first
      if (this.pages.length === 0) {
        await this.loadPages();
      }
      
      // Load existing permissions
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.permission_groups_show + group.id,
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        console.log('Full API Response:', response.data);
        
        // Handle different response structures
        let permissions = [];
        if (response.data?.result?.data?.group?.permissions) {
          permissions = response.data.result.data.group.permissions;
        } else if (response.data?.data?.group?.permissions) {
          permissions = response.data.data.group.permissions;
        } else if (response.data?.group?.permissions) {
          permissions = response.data.group.permissions;
        }
        
        console.log('Extracted permissions:', permissions);
        console.log('Available pages:', this.pages.map(p => ({ id: p.id, name: p.name })));
        
        // Initialize permissionsData with all pages set to false first
        // Use consistent type (number) for page IDs
        this.pages.forEach(page => {
          const pageId = typeof page.id === 'string' ? parseInt(page.id) : page.id;
          this.permissionsData[pageId] = {
            read: false,
            create: false,
            update: false,
            delete: false,
          };
        });
        
        // Then update with existing permissions
        permissions.forEach(perm => {
          const pageId = typeof perm.page_id === 'string' ? parseInt(perm.page_id) : perm.page_id;
          if (this.permissionsData[pageId]) {
            this.permissionsData[pageId] = {
              read: perm.can_read === true || perm.can_read === 1,
              create: perm.can_create === true || perm.can_create === 1,
              update: perm.can_update === true || perm.can_update === 1,
              delete: perm.can_delete === true || perm.can_delete === 1,
            };
          } else {
            console.warn(`Page ID ${pageId} not found in pages list. Permission:`, perm);
          }
        });
        
        console.log('Final permissionsData:', this.permissionsData);
      } catch (error) {
        console.error('Error loading permissions:', error);
        console.error('Error details:', error.response?.data);
        // Initialize with all false if error
        this.pages.forEach(page => {
          const pageId = typeof page.id === 'string' ? parseInt(page.id) : page.id;
          this.permissionsData[pageId] = {
            read: false,
            create: false,
            update: false,
            delete: false,
          };
        });
      }
      
      this.showPermissionsModal = true;
      this.$nextTick(() => {
        this.updateIndeterminateState();
      });
    },
    closePermissionsModal() {
      this.showPermissionsModal = false;
      this.selectedGroup = null;
      this.permissionsData = {};
    },
    getPagePermission(pageId, action) {
      // Ensure pageId is treated as a number for consistent lookup
      const id = typeof pageId === 'string' ? parseInt(pageId) : pageId;
      return this.permissionsData[id]?.[action] === true || this.permissionsData[id]?.[action] === 1;
    },
    updatePermission(pageId, action, value) {
      // Ensure pageId is treated as a number for consistent lookup
      const id = typeof pageId === 'string' ? parseInt(pageId) : pageId;
      if (!this.permissionsData[id]) {
        this.permissionsData[id] = {
          read: false,
          create: false,
          update: false,
          delete: false,
        };
      }
      this.permissionsData[id][action] = value === true || value === 1;
    },
    // Check if all pages have this permission checked
    isAllChecked(action) {
      if (this.pages.length === 0) return false;
      return this.pages.every(page => {
        return this.getPagePermission(page.id, action) === true;
      });
    },
    // Check if some (but not all) pages have this permission checked (indeterminate state)
    isIndeterminate(action) {
      if (this.pages.length === 0) return false;
      const checkedCount = this.pages.filter(page => {
        return this.getPagePermission(page.id, action) === true;
      }).length;
      return checkedCount > 0 && checkedCount < this.pages.length;
    },
    // Toggle all permissions for a specific action (column)
    toggleAllPermissions(action, checked) {
      this.pages.forEach(page => {
        this.updatePermission(page.id, action, checked);
      });
      this.$nextTick(() => {
        this.updateIndeterminateState();
      });
    },
    // Update indeterminate state for header checkboxes
    updateIndeterminateState() {
      const actions = ['read', 'create', 'update', 'delete'];
      const refs = {
        read: 'readCheckbox',
        create: 'createCheckbox',
        update: 'updateCheckbox',
        delete: 'deleteCheckbox',
      };
      
      actions.forEach(action => {
        const checkbox = this.$refs[refs[action]];
        if (checkbox && Array.isArray(checkbox) && checkbox.length > 0) {
          checkbox[0].indeterminate = this.isIndeterminate(action);
        } else if (checkbox) {
          checkbox.indeterminate = this.isIndeterminate(action);
        }
      });
    },
    async savePermissions() {
      try {
        const token = localStorage.getItem('token');
        const permissions = Object.keys(this.permissionsData).map(pageId => ({
          page_id: parseInt(pageId),
          can_read: this.permissionsData[pageId].read || false,
          can_create: this.permissionsData[pageId].create || false,
          can_update: this.permissionsData[pageId].update || false,
          can_delete: this.permissionsData[pageId].delete || false,
        }));
        
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.permission_groups_assign_permissions + this.selectedGroup.id + '/permissions',
          { permissions },
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        this.closePermissionsModal();
        this.loadGroups();
      } catch (error) {
        console.error('Error saving permissions:', error);
        alert(error.response?.data?.result?.mesAr || 'حدث خطأ أثناء حفظ الصلاحيات');
      }
    },
  },
};
</script>

